/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Alert } from 'antd'
import CreditCardInput from '../../components/CreditCardInput'
import Icon from '../../components/Icon'
import stripeLogo from './stripe_logo.svg'
import PaymentError from './components/PaymentError'

import { useI18n } from '@shopify/react-i18n'
import { ROUTES } from '@sportninja/common/constants/app'
import { PAYMENT_ATTEMPT_COPY } from '@sportninja/common/features/registration/payment/constants/paymentAttemptCopy'
import { isRetryablePaymentError } from '@sportninja/common/features/registration/payment/utils/paymentAttemptState'
import { safeReturnPath } from '@sportninja/common/features/registration/payment/utils/safeReturnPath'
import { useCountries } from '@sportninja/common/hooks/useCountries'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { getBillingTypeLabel } from '@sportninja/common/utils/billing'
import { PAYMENT_PROVIDERS } from 'src/hooks/usePaymentProvider'
import useSinglePaymentProvider from 'src/hooks/useSinglePaymentProvider'
import LoadingSpinner from '../../components/LoadingSpinner'
import {
  getFeeLineItems,
  splitFeeLineItemsForReceipt,
} from '../RegistrationInviteV2/utils/feeLineItems'

const PAYMENT_STATUS = {
  SUCCESS: 'success',
}

/** Sep 24 — an attempt already under way that is not a failure gets a calmer glyph. */
const ATTEMPT_ICONS = {
  processing: 'clock',
  received: 'check-circle',
  paid: 'check-circle',
}

const RegistrationPayment = () => {
  const history = useHistory()
  const [i18n] = useI18n()

  const { search } = useLocation()

  // State for credit card input values (for SportsPay)
  const [creditCardValues, setCreditCardValues] = useState({
    cardNumber: '',
    expiryDate: '',
    cvc: '',
    country: '',
    countryCode: '',
    zipCode: '',
    cardType: '',
    errors: {},
  })

  const params = useMemo(() => {
    // Full-balance ("Pay Full Balance") mode: ?teamRegistrationId=...
    const payInFull = /[?&]teamRegistrationId=([^&]+)/.exec(search)
    if (payInFull) {
      return { teamRegistrationId: payInFull[1] }
    }

    // Prefer new format: ?billingId=...
    const simple = /\?billingId=([^&]+)/.exec(search)
    if (simple) {
      return { billingId: simple[1] }
    }

    // Fallback to legacy formats for backward compatibility
    const divisionRegex =
      /\?divisionId=([^&]+)&teamId=([^&]+)&billingId=([^&]+)/
    const divisionMatch = divisionRegex.exec(search)
    if (divisionMatch) {
      return { billingId: divisionMatch[3] }
    }

    const billingRegex = /\?teamId=([^&]+)&rosterId=([^&]+)&billingId=([^&]+)/
    const billingMatch = billingRegex.exec(search)
    if (billingMatch) {
      return { billingId: billingMatch[3] }
    }
  }, [search])

  const {
    billingSchedule,
    error,
    paymentIsLoading,
    paymentStatus,
    paymentError,
    attemptState,
    bootstrapFinished,
    reloadIntent,
    paymentProvider,
    processSportsPayPayment,
    processStripePayment,
  } = useSinglePaymentProvider({
    billingId: params?.billingId,
    teamRegistrationId: params?.teamRegistrationId,
  })

  // Sep 24 — where the payer goes when they are done: back into the flow that sent
  // them here (`?returnTo=`, a same-origin path only), or Home as always.
  const returnTo = useMemo(
    () =>
      safeReturnPath(
        new URLSearchParams(search).get('returnTo'),
        window.location.origin
      ),
    [search]
  )
  const leavePage = () => history.replace(returnTo ?? ROUTES.HOME)

  const billing = billingSchedule

  // Registrant-facing fee rows (League/Payment Processing Fee) from the billing
  // payload. League fees are taxed and render above Tax; payment processing is
  // struck on the final amount and renders below Tax — same split as the
  // registration checkout receipt (single source of truth in feeLineItems.ts).
  const feeLineItems = getFeeLineItems(billing)
  const { taxedFees, postTaxFees } = splitFeeLineItemsForReceipt(feeLineItems)

  // Consolidated 'full' rows are a stored contract: display the stamped tax and
  // total — exactly what the server charges — instead of recomputing current rates
  const isFullPaymentRow = billing?.payment_type === 'full'
  // SN-8310 — a rep's added invoice says so, rather than the raw 'additional', even
  // on a one-time plan: it is an extra charge, not that plan's one payment.
  const isAdditionalInvoice = billing?.payment_type === 'additional'
  const paymentTypeLabel =
    isFullPaymentRow || isAdditionalInvoice
      ? getBillingTypeLabel({ payment_type: billing?.payment_type })
      : billing?.payment_type
  // Tax on top — a Team-Managed Budget invoice is the same kind of stored contract:
  // its tax was fixed when the rep invoiced it (and an invoice issued before tax
  // moved on top keeps the tax that was inside its share). Re-taxing its subtotal
  // at today's rate could make the lines disagree with the amount charged.
  const usesStoredTax = isFullPaymentRow || billing?.cost_distribution === true

  const { countries, refetch: refetchCountries } = useCountries()
  const [localError, setLocalError] = useState(null)

  // Combine all error sources for display
  const displayError = localError || paymentError

  useEffect(() => {
    if (countries && countries.length === 0) {
      refetchCountries()
    }
  }, [countries, refetchCountries])

  // Handle credit card input changes
  const handleCreditCardChange = (values) => {
    setCreditCardValues(values)
  }

  // Format credit card data for SportsPay
  const formatSportsPayCardData = useCallback(() => {
    if (!creditCardValues) {
      return null
    }

    // Extract month and year from expiry date (MM / YY format)
    const expiryParts = creditCardValues.expiryDate.split(' / ')
    const month = expiryParts[0]
    const year = expiryParts[1] ? `20${expiryParts[1]}` : ''

    return {
      CARDNUM: creditCardValues.cardNumber.replace(/\s+/g, ''),
      CARDEXPMM: month,
      CARDEXPYYYY: year,
      CARDCVV: creditCardValues.cvc,
    }
  }, [creditCardValues])

  // A US billing ZIP is mandatory; block submit until it is present and valid
  const isBillingZipMissing =
    paymentProvider === PAYMENT_PROVIDERS.SPORTS_PAY &&
    !!creditCardValues.errors?.zipCode

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    setLocalError(null)
    if (paymentProvider === PAYMENT_PROVIDERS.STRIPE) {
      await processStripePayment()
      return
    }
    if (paymentProvider === PAYMENT_PROVIDERS.SPORTS_PAY) {
      // Check for credit card validation errors
      if (creditCardValues.errors?.expiryDate) {
        setLocalError(creditCardValues.errors.expiryDate)
        return
      }

      // Billing ZIP is mandatory for US cards
      if (creditCardValues.errors?.zipCode) {
        setLocalError(creditCardValues.errors.zipCode)
        return
      }

      // Format card data for SportsPay
      const cardData = formatSportsPayCardData()
      if (!cardData) {
        setLocalError('Please enter valid card details')
        return
      }

      // SportsPay payment processing
      await processSportsPayPayment(cardData, {
        postalCode: creditCardValues.zipCode || undefined,
        countryCode: creditCardValues.countryCode || undefined,
      })
    }
  }

  if (!params) {
    return (
      <PaymentError
        title='Payment Error'
        message={'Billing Not Found'}
        onPrimaryAction={() => history.replace(ROUTES.HOME)}
        buttonLabel='Back to Home'
      />
    )
  }

  if (attemptState) {
    const copy = PAYMENT_ATTEMPT_COPY[attemptState]
    const iconName = ATTEMPT_ICONS[attemptState]

    return (
      <PaymentError
        title={copy.title}
        message={copy.subtitle}
        icon={
          iconName ? (
            <Icon name={iconName} color={colors.DEFAULT_FLAIR} fontSize={48} />
          ) : undefined
        }
        onPrimaryAction={leavePage}
        buttonLabel={returnTo ? 'Continue' : 'Back to Home'}
      />
    )
  }

  if (error) {
    return (
      <PaymentError
        title='Payment Error'
        message={
          getErrorMessage(error) || error?.message || 'Something went wrong'
        }
        onPrimaryAction={leavePage}
        buttonLabel={returnTo ? 'Continue' : 'Back to Home'}
        retryAction={
          isRetryablePaymentError(error)
            ? { label: 'Try Again', onClick: reloadIntent }
            : undefined
        }
      />
    )
  }

  const orgImageUrl = resolveAssetUrl(billing?.organization_image)
  const rootScheduleImageUrl = resolveAssetUrl(billing?.root_schedule_image)

  return (
    <div
      css={css`
        display: flex;
        height: 100vh;

        @media (max-width: 768px) {
          flex-direction: column;
          height: 100%;
        }
      `}
    >
      <div
        css={css`
          width: 50%;
          padding-top: 80px;
          padding-left: 106px;
          padding-right: 140px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;

          @media (max-width: 768px) {
            width: 100%;
            padding-left: 20px;
            padding-right: 20px;
          }
        `}
      >
        <div
          onClick={() => history.replace(ROUTES.HOME)}
          css={css`
            display: flex;
            align-items: center;

            &:hover {
              cursor: pointer;
            }
          `}
        >
          <Icon name='home' color={colors.DEFAULT_FLAIR} fontSize={12} />
          <p
            css={css`
              font-size: 12px;
              font-weight: 500;
              margin-left: 12px;
              color: ${colors.DEFAULT_FLAIR};
            `}
          >
            GO HOME
          </p>
        </div>

        <div>
          {billing?.subtotal ? (
            <>
              {orgImageUrl && (
                <img
                  src={orgImageUrl}
                  alt={billing?.organization_name + ' League Logo'}
                  css={css`
                    margin-top: 32px;
                    width: 160px;
                    height: auto;
                    max-height: 160px;
                  `}
                />
              )}
              <div
                css={css`
                  margin-top: 56px;
                `}
              >
                <p
                  css={css`
                    font-weight: 700;
                    font-size: 18px;
                  `}
                >
                  Paying{' '}
                  {billing?.single_payment && !isAdditionalInvoice
                    ? ''
                    : paymentTypeLabel}
                  {' to '}
                  {billing?.organization_name
                    ? billing?.organization_name
                    : 'SportNinja'}
                </p>
                <p
                  css={css`
                    font-weight: 700;
                    font-size: 40px;
                    margin-top: 14px;
                  `}
                >
                  {i18n.formatCurrency(
                    // Stored-tax rows charge the stored total including tax — the
                    // headline amount must match what the card is charged
                    usesStoredTax ? billing?.total : billing?.subtotal,
                    {
                      currency: billing?.currency_code?.toUpperCase(),
                      form: 'short',
                    }
                  )}
                </p>
              </div>
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  margin-top: 56px;
                `}
              >
                {rootScheduleImageUrl && (
                  <img
                    src={rootScheduleImageUrl}
                    css={css`
                      width: 50px;
                      height: auto;
                      margin-right: 16px;
                    `}
                  />
                )}
                <p
                  css={css`
                    font-size: 16px;
                    font-weight: 700;
                  `}
                >
                  {`${billing?.root_schedule_name} - ${billing?.division_name}`}
                </p>
              </div>
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  margin-top: 56px;
                  padding-bottom: 8px;
                `}
              >
                <p
                  css={css`
                    font-size: 18px;
                    text-transform: capitalize;
                    text-align: right;
                    flex: 1;

                    @media (max-width: 768px) {
                      text-align: left;
                    }
                  `}
                >
                  {paymentTypeLabel || 'Subtotal'}
                </p>
                <p
                  css={css`
                    font-size: 18px;
                    flex: 1;
                    text-align: right;
                  `}
                >
                  {i18n.formatCurrency(billing?.subtotal, {
                    currency: billing?.currency_code?.toUpperCase(),
                    form: 'explicit',
                  })}
                </p>
              </div>
              {/* League fees sit above Tax (taxed). Keyed off fee identity via
                  splitFeeLineItemsForReceipt, not API order. Secondary lines
                  (fees + Tax) render at 14px, tightly spaced. */}
              {taxedFees.map((fee) => (
                <div
                  key={fee.key}
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-top: 4px;
                  `}
                >
                  <p
                    css={css`
                      font-size: 14px;
                      text-align: right;
                      flex: 1;

                      @media (max-width: 768px) {
                        text-align: left;
                      }
                    `}
                  >
                    {fee.label}
                  </p>
                  <p
                    css={css`
                      font-size: 14px;
                      flex: 1;
                      text-align: right;
                    `}
                  >
                    {i18n.formatCurrency(fee.amount, {
                      currency: billing?.currency_code?.toUpperCase(),
                      form: 'explicit',
                    })}
                  </p>
                </div>
              ))}
              {usesStoredTax && Number(billing?.taxtotal) > 0 ? (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-top: 4px;
                  `}
                >
                  <p
                    css={css`
                      font-size: 14px;
                      text-align: right;
                      flex: 1;
                      align-items: center;

                      @media (max-width: 768px) {
                        text-align: left;
                      }
                    `}
                  >
                    Tax
                  </p>
                  <p
                    css={css`
                      font-size: 14px;
                      flex: 1;
                      text-align: right;
                    `}
                  >
                    {i18n.formatCurrency(billing?.taxtotal, {
                      currency: billing?.currency_code?.toUpperCase(),
                      form: 'explicit',
                    })}
                  </p>
                </div>
              ) : null}
              {!usesStoredTax &&
                billing?.taxes.map((tax) => (
                  <div
                    key={tax?.uid}
                    css={css`
                      display: flex;
                      align-items: center;
                      justify-content: space-between;
                      margin-top: 4px;
                    `}
                  >
                    <p
                      css={css`
                        font-size: 14px;
                        text-transform: capitalize;
                        text-align: right;
                        flex: 1;
                        align-items: center;

                        @media (max-width: 768px) {
                          text-align: left;
                        }
                      `}
                    >
                      {tax?.name} - {tax?.description} (
                      {Number(tax?.tax_rate).toFixed(1)}%)
                    </p>
                    <p
                      css={css`
                        font-size: 14px;
                        flex: 1;
                        text-align: right;
                      `}
                    >
                      {i18n.formatCurrency(
                        billing?.subtotal * (Number(tax?.tax_rate) / 100),
                        {
                          currency: billing?.currency_code?.toUpperCase(),
                          form: 'explicit',
                        }
                      )}
                    </p>
                  </div>
                ))}
              {/* Payment processing fee sits below Tax (struck on the final
                  amount). Same identity-based split as the checkout receipt. */}
              {postTaxFees.map((fee) => (
                <div
                  key={fee.key}
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-top: 4px;
                  `}
                >
                  <p
                    css={css`
                      font-size: 14px;
                      text-align: right;
                      flex: 1;

                      @media (max-width: 768px) {
                        text-align: left;
                      }
                    `}
                  >
                    {fee.label}
                  </p>
                  <p
                    css={css`
                      font-size: 14px;
                      flex: 1;
                      text-align: right;
                    `}
                  >
                    {i18n.formatCurrency(fee.amount, {
                      currency: billing?.currency_code?.toUpperCase(),
                      form: 'explicit',
                    })}
                  </p>
                </div>
              ))}
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  margin-top: 16px;
                  padding-top: 16px;
                  border-top: 1px solid ${colors.SOFT_STEEL};
                `}
              >
                <p
                  css={css`
                    font-size: 18px;
                    font-weight: 700;
                    flex: 1;
                    text-align: right;

                    @media (max-width: 768px) {
                      text-align: left;
                    }
                  `}
                >
                  Total
                </p>
                <p
                  css={css`
                    font-size: 18px;
                    font-weight: 700;
                    flex: 1;
                    text-align: right;
                  `}
                >
                  {/* {i18n.formatCurrency(
                    isFullPaymentRow
                      ? billing?.total
                      : billing?.subtotal +
                          billing?.subtotal * (totalTaxSum / 100),
                    {
                      currency: billing?.currency_code?.toUpperCase(),
                      form: 'explicit',
                    }
                  )} */}
                  {i18n.formatCurrency(billing?.total, {
                    currency: billing?.currency_code?.toUpperCase(),
                    form: 'explicit',
                  })}
                </p>
              </div>
            </>
          ) : (
            <LoadingSpinner />
          )}
        </div>
        <div
          css={css`
            padding-bottom: 106px;
            display: flex;
            align-items: center;

            @media (max-width: 768px) {
              display: none;
            }
          `}
        >
          <p>Powered by</p>
          {paymentProvider === PAYMENT_PROVIDERS.SPORTS_PAY && (
            <img
              src='/images/sportspay.png'
              css={css`
                margin-left: 6px;
                width: 32px;
                height: 32px;
              `}
            />
          )}
          {paymentProvider === PAYMENT_PROVIDERS.STRIPE && (
            <img
              src={stripeLogo}
              css={css`
                margin-left: 6px;
              `}
            />
          )}
        </div>
      </div>

      <div
        css={css`
          height: 100%;
          width: 1px;
          background-color: ${colors.SOFT_STEEL};
        `}
      />

      <div
        css={css`
          width: 50%;
          padding-top: 80px;
          padding-left: 106px;
          padding-right: 140px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;

          @media (max-width: 768px) {
            width: 100%;
            padding-left: 20px;
            padding-right: 20px;
            padding-top: 0px;
          }
        `}
      >
        <div
          css={css`
            margin-top: 32px;
            width: 100%;
          `}
        >
          {paymentStatus === PAYMENT_STATUS.SUCCESS ? (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
              `}
            >
              <Icon name='check' color={colors.DEFAULT_FLAIR} fontSize={48} />
              <p
                css={css`
                  margin-top: 32px;
                  font-size: 24px;
                  font-weight: 700;
                `}
              >
                Payment Successful
              </p>
              <button
                onClick={leavePage}
                css={css`
                  margin-top: 100px;
                  display: flex;
                  flex-direction: row;
                  justify-content: center;
                  align-items: center;
                  padding: 8px 24px;
                  border: 2px solid ${colors.PRIMARY};
                  border-radius: 4px;
                  height: 48px;
                  width: 100%;
                  color: ${colors.WHITE};
                `}
              >
                {returnTo ? 'CONTINUE' : 'GO BACK TO HOME'}
              </button>
            </div>
          ) : (
            <div
              css={css`
                position: relative;
                min-height: 350px;
              `}
            >
              <div
                css={css`
                  visibility: ${bootstrapFinished ? 'visible' : 'hidden'};
                  contain: layout;
                `}
              >
                <form id='payment-form' onSubmit={handleSubmit}>
                  {/* Show our custom CreditCardInput for SportsPay */}
                  {paymentProvider === PAYMENT_PROVIDERS.SPORTS_PAY && (
                    <CreditCardInput
                      onChange={handleCreditCardChange}
                      companyName={billing?.organization_name || 'SportNinja'}
                      countries={countries}
                    />
                  )}

                  <div id='card-element' />
                  {/* Only show Stripe payment element if using Stripe */}
                  {paymentProvider === PAYMENT_PROVIDERS.STRIPE && (
                    <div
                      id='payment-element'
                      css={css`
                        min-height: 300px;
                      `}
                    />
                  )}
                  <button
                    disabled={paymentIsLoading || isBillingZipMissing}
                    id='submit'
                    css={css`
                      height: 40px;
                      display: flex;
                      padding: 8px 24px;
                      justify-content: center;
                      align-items: center;
                      gap: 8px;
                      flex: 1 0 0;
                      border-radius: 8px;
                      cursor: ${paymentIsLoading ? 'not-allowed' : 'pointer'};
                      background-color: ${colors.PRIMARY};
                      color: ${PRIMARY_BUTTON_CONTENT.text};
                      text-transform: uppercase;
                      margin-top: 16px;
                      width: 100%;
                      ${paymentIsLoading &&
                      css`
                        background-color: ${colors.SECONDARY_200};
                        color: ${colors.WHITE};
                      `}
                    `}
                  >
                    {paymentIsLoading ? <LoadingSpinner size={2} /> : 'PAY NOW'}
                  </button>
                </form>
              </div>
              {!bootstrapFinished && (
                <div
                  css={css`
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                  `}
                >
                  <LoadingSpinner />
                </div>
              )}
            </div>
          )}
        </div>
        {displayError && (
          <Alert
            message={displayError}
            type='error'
            showIcon
            css={css`
              margin-top: 16px;
              width: 100%;
            `}
          />
        )}
      </div>
    </div>
  )
}

export default RegistrationPayment
